import React from 'react';
import { Select } from 'grab-ui';
import cx from 'classnames';
import homeSvg from 'public/images/home.svg';
import workSvg from 'public/images/work.svg';
import frequentSvg from 'public/images/frequent.svg';
import poiSvg from 'public/images/poi.svg';
import savedSvg from 'public/images/saved.svg';
import frequentGreySvg from 'public/images/frequent-grey.svg';
import { SCRIBE_STATE_NAMES } from 'applications/scribe/instrumentations/track';
import SavedPoiCTAButton from 'components/v2/saved-poi-cta-button';
import _get from 'lodash/get';
import { SAVED_PLACE_PRESET_I18N_TAGS } from 'constants/app-constants';
import I18n from 'i18n-js';
import styles from './styles.less';

const Option = Select.Option;

const getAddressNameOrLabel = poi => {
  const label = _get(poi, 'label');
  const i18nTag = _get(SAVED_PLACE_PRESET_I18N_TAGS, label);
  if (i18nTag) {
    return I18n.t(i18nTag);
  }
  return label || _get(poi, 'address.name');
};

const getIcon = (poi, isChild) => {
  const category = _get(poi, 'category');
  const label = _get(poi, 'label');

  if (category === 'Saved places' || label) {
    if (isChild) {
      return undefined;
    }
    if (label === 'HOME_TAG_PRESET') {
      return homeSvg;
    }
    if (label === 'WORK_TAG_PRESET') {
      return workSvg;
    }
    return savedSvg;
  }
  if (category === 'Frequently used') {
    return isChild ? frequentGreySvg : frequentSvg;
  }
  if (!isChild) return poiSvg;
};

export const renderChildPOIOption = (
  childPOI,
  postSavingPoiCallback,
  isPickUp,
) => (
  <Option
    key={childPOI.id}
    value={childPOI.id}
    className={cx(styles.addressOption, styles.addressSubOption)}
  >
    {renderSubAddressOption(childPOI)}
    <SavedPoiCTAButton
      poi={childPOI}
      postSavingPoiCallback={postSavingPoiCallback}
      isPickUp={isPickUp}
      scribeState={SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB}
    />
  </Option>
);

export const renderRootPOIOption = (
  rootPOI,
  hasChildren,
  postSavingPoiCallback,
  isPickUp,
) => {
  if (!rootPOI) {
    return null;
  }

  if (hasChildren) {
    return (
      <Option
        key={rootPOI.id}
        value={rootPOI.id}
        disabled
        className={styles.addressOption}
      >
        <img className={styles.poiIcon} src={getIcon(rootPOI, false)} alt="" />
        {renderAddressOption(rootPOI)}
      </Option>
    );
  }

  return (
    <Option
      key={rootPOI.id}
      value={rootPOI.id}
      className={styles.addressOption}
    >
      <img className={styles.poiIcon} src={getIcon(rootPOI, false)} alt="" />
      <div className={styles.addressWithBorder}>
        {renderAddressOption(rootPOI)}
        <div className={styles.border} />
      </div>
      <SavedPoiCTAButton
        poi={rootPOI}
        postSavingPoiCallback={postSavingPoiCallback}
        isPickUp={isPickUp}
        scribeState={SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB}
      />
    </Option>
  );
};

const renderSubAddressOption = poi => (
  <div
    className={cx(styles.addressOptionContent, styles.addressSubOptionContent)}
  >
    <h5 className={styles.addressChildInfo}>{getAddressNameOrLabel(poi)}</h5>
    <div className={styles.border} />
  </div>
);

const renderAddressOption = poi => (
  <div className={styles.addressOptionContent}>
    <h5 className={styles.addressOptionInfo}>{getAddressNameOrLabel(poi)}</h5>
    <h6 className={styles.addressOptionSubInfo}>
      {_get(poi, 'address.combined_address')}
    </h6>
  </div>
);

const prioritizePOICategory = (currentPOI, targetPOI) => {
  // Will return current or target
  const prioritizeList = ['Saved places', 'Frequently used'];
  if (currentPOI === prioritizeList[0] || targetPOI === prioritizeList[0]) {
    return prioritizeList[0];
  } else if (targetPOI === prioritizeList[1]) {
    return prioritizeList[1];
  }
  return currentPOI;
};

export const processPOIResponse = (response, isSuggestPOI) => {
  const { result, extra, pois, trees } = response;

  const isParent = {};
  const childToParent = {};
  const poiLookUp = {};
  const poiCategory = {};

  if (trees) {
    trees.forEach(parentPOI => {
      isParent[parentPOI.id] = true;
      if (parentPOI.children && Array.isArray(parentPOI.children)) {
        parentPOI.children.forEach(poi => {
          childToParent[poi.id] = parentPOI.id;
        });
      }
    });
  }

  if (pois) {
    pois.forEach(({ id: poiID, root: parentID, category }) => {
      isParent[parentID] = true;
      childToParent[poiID] = parentID;

      if (isSuggestPOI) {
        const currentPOI = _get(poiCategory, poiID);
        if (currentPOI) {
          // a place has multiple categories, prioritize Saved -> Frequently -> Others
          poiCategory[poiID] = prioritizePOICategory(currentPOI, category);
        } else {
          poiCategory[poiID] = category;
        }
      }
    });
  }

  if (extra && Array.isArray(extra)) {
    extra.forEach(poi => {
      if (isSuggestPOI) {
        poi.category = poiCategory[poi.id];
      }
      poiLookUp[poi.id] = poi;
    });
  }

  if (result && Array.isArray(result)) {
    result.forEach(poi => {
      if (isSuggestPOI) {
        poi.category = poiCategory[poi.id];
      }
      poiLookUp[poi.id] = poi;
    });
  }
  const rootPOIs = [];
  const parentPOIAdded = {};
  const childrenPOIs = {};
  const childPOIAdded = {};

  const pushParentPoiOnce = parentPoiID => {
    if (!parentPOIAdded[parentPoiID] && poiLookUp[parentPoiID]) {
      rootPOIs.push(poiLookUp[parentPoiID]);
      parentPOIAdded[parentPoiID] = true;
    }
  };

  const processList = isSuggestPOI ? pois : result;

  if (processList) {
    processList.forEach(poi => {
      if (isParent[poi.id]) {
        // parent poi
        pushParentPoiOnce(poi.id);
      } else if (childToParent[poi.id]) {
        // child poi
        const parentPoiID = childToParent[poi.id];
        pushParentPoiOnce(parentPoiID);
        if (!childrenPOIs[parentPoiID]) {
          childrenPOIs[parentPoiID] = [];
        }
        if (!childPOIAdded[poi.id]) {
          // should add child once
          childrenPOIs[parentPoiID].push(poiLookUp[poi.id]);
          childPOIAdded[poi.id] = true;
        }
      } else {
        // standalone poi
        rootPOIs.push(poiLookUp[poi.id]);
      }
    });
  }

  return {
    rootPOIs,
    childrenPOIs,
  };
};
